/* ============================================================================
 * views/Riders.jsx — Page 8: Riders
 * ----------------------------------------------------------------------------
 * Riders table on the left. When a row is selected, a detail panel on the
 * right shows three sections:
 *   A) Rider info — editable form (update_rider RPC)
 *   B) Weekly availability — upsert_rider_availability / delete_rider_availability
 *   C) Errands — create_errand / update_errand / soft_delete_errand
 *
 * No hardcoded enum restrictions: service_type, errand_type, and status
 * are entered as free text (with helpful datalist suggestions). The DB
 * check constraints will enforce validity.
 * ========================================================================= */

import React from 'react'
import { humanize, formatPhone, validatePhone, formatMoney, formatDate, ENUM_OPTIONS } from '../utils.jsx'
import { Riders } from '../db.jsx'
import { Pill, StatusDot, statusTone, Drawer, ConfirmModal, Field, EnumSelect, PhoneInput, EmptyState, Spinner, FilterBar, useFilteredRows, useToast } from '../components.jsx'
import { I } from '../icons.jsx'

const DAYS = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
const SERVICE_SUGGESTIONS = ["lalamove","grab","maxim","joyride","toktok","foodpanda","individual","personal","other"];

function RidersView() {
  const [rows, setRows] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [filters, setFilters] = React.useState({ q: "", service_type: "", active: "" });

  const refresh = React.useCallback(() => {
    return Riders.list().then(r => {
      setRows(r.data || []);
      // Re-sync the selected row from the latest list so the detail panel
      // always reflects the most recent fields.
      if (r.data && selected) {
        const fresh = r.data.find(x => x.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    });
  }, [selected]);

  React.useEffect(() => {
    const ctrl = new AbortController();
    Riders.list(ctrl.signal).then(r => setRows(r.data || [])).catch(() => setRows([]));
    return () => ctrl.abort();
  }, []);

  const filterDefs = React.useMemo(() => ([
    { type: "search", key: "q", placeholder: "Search rider name or phone…", fields: ["name", "phone"] },
    { type: "select", key: "service_type", label: "All service types", options: ENUM_OPTIONS.service_type },
    { type: "select", key: "active", label: "Status", options: ["active", "inactive"] },
  ]), []);
  const baseFiltered = useFilteredRows(rows, filterDefs.slice(0, 2), filters);
  const filtered = (baseFiltered || []).filter(r => {
    if (filters.active === "active") return r.active;
    if (filters.active === "inactive") return !r.active;
    return true;
  });

  return (
    <div className={"riders-layout" + (selected ? " has-detail" : " single")}>
      <div className="rider-list-col">
        <FilterBar filters={filterDefs} values={filters} onChange={setFilters}>
          <button className="btn btn-primary" onClick={() => setCreating(true)}>{I.plus }New rider</button>
        </FilterBar>
        <div className="card"><div className="tbl-wrap">
          {rows === null ? <Spinner/> :
           filtered.length === 0 ? <EmptyState title="No riders" hint={rows.length ? "No riders match the current filters." : "Add your first rider to start tracking deliveries and errands."}/> :
           <table className="tbl">
             <thead><tr><th>Name</th><th>Phone</th><th>Service</th><th>Base fee</th><th>Per-km</th><th>Always</th><th>Active</th><th>Pending</th><th>Active dlvr</th></tr></thead>
             <tbody>{filtered.map(r => (
               <tr key={r.id}
                   className={selected?.id === r.id ? "selected" : ""}
                   style={{ background: selected?.id === r.id ? "var(--row-hover)" : undefined }}
                   onClick={() => setSelected(r)}>
                 <td style={{ fontWeight: 600 }}>{r.name}</td>
                 <td className="mono" style={{ fontSize: 12 }}>{r.phone || "—"}</td>
                 <td>{humanize(r.service_type || "—")}</td>
                 <td>{r.base_fee != null ? formatMoney(r.base_fee) : "—"}</td>
                 <td>{r.per_km_fee != null ? formatMoney(r.per_km_fee) : "—"}</td>
                 <td>{r.is_always_available ? <Pill tone="ok">Yes</Pill> : <Pill>No</Pill>}</td>
                 <td>{r.active ? <Pill tone="ok">Active</Pill> : <Pill tone="danger">Inactive</Pill>}</td>
                 <td className="mono">{r.pending_errands ?? 0}</td>
                 <td className="mono">{r.active_deliveries ?? 0}</td>
               </tr>))}</tbody>
           </table>}
        </div></div>
      </div>

      {selected && (
        <RiderDetail
          rider={selected}
          onClose={() => setSelected(null)}
          onChanged={refresh}
        />
      )}


      {creating && (
        <RiderDrawer
          rider={{}} isNew
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); refresh(); }}
        />
      )}
    </div>
  );
}

/* ---------- Detail panel (Sections A/B/C) -------------------------------- */
function RiderDetail({ rider, onClose, onChanged }) {
  const [editing, setEditing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = React.useState(false);

  async function doSoftDeactivate() {
    setBusy(true);
    const res = await Riders.update(rider.id, { ...rider, active: false });
    setBusy(false);
    if (!res.error) { onClose(); onChanged(); }
  }

  return (
    <div style={{ display: "grid", gap: 16, alignContent: "start" }}>
      <div className="riders-detail-back" onClick={onClose}>← Back to riders</div>
      {/* Section A — Rider info */}
      <div className="card">
        <div className="card-head row between" style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{rider.name}</div>
            <div className="muted" style={{ fontSize: 12 }}>{rider.phone || "no phone"} · {humanize(rider.service_type || "—")}</div>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => setEditing(true)}>Edit info</button>
            <button className="btn btn-ghost" onClick={() => setConfirmDeactivate(true)} disabled={busy}>Deactivate</button>
            <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">{I.x}</button>
          </div>
        </div>
        <div style={{ padding: 16 }}>
          <div className="grid c3" style={{ gap: 12 }}>
            <KV label="Base fee" value={rider.base_fee != null ? formatMoney(rider.base_fee) : "—"}/>
            <KV label="Per-km fee" value={rider.per_km_fee != null ? formatMoney(rider.per_km_fee) : "—"}/>
            <KV label="Always available" value={rider.is_always_available ? "Yes" : "No"}/>
            <KV label="Pending errands" value={rider.pending_errands ?? 0}/>
            <KV label="Active deliveries" value={rider.active_deliveries ?? 0}/>
            <KV label="Status" value={rider.active ? <Pill tone="ok">Active</Pill> : <Pill tone="danger">Inactive</Pill>}/>
          </div>
          {rider.notes && <div style={{ marginTop: 12 }}><div className="muted" style={{ fontSize: 11 }}>Notes</div><div>{rider.notes}</div></div>}
        </div>
      </div>

      {/* Section B — Availability */}
      <AvailabilitySection rider={rider}/>

      {/* Section C — Assigned deliveries */}
      <AssignedDeliveriesSection rider={rider}/>

      {/* Section D — Errands */}
      <ErrandsSection rider={rider}/>

      {editing && (
        <RiderDrawer
          rider={rider}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged(); }}
        />
      )}
      {confirmDeactivate && (
        <ConfirmModal title="Deactivate rider" message={`Deactivate rider ${rider.name}? They will no longer be assignable to errands.`}
          confirmLabel="Deactivate"
          onConfirm={doSoftDeactivate}
          onClose={() => setConfirmDeactivate(false)} />
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (<div><div className="muted" style={{ fontSize: 11 }}>{label}</div><div style={{ fontWeight: 600 }}>{value}</div></div>);
}

/* ---------- Rider create/edit drawer ------------------------------------- */
function RiderDrawer({ rider, isNew, onClose, onSaved }) {
  const toast = useToast();
  const [f, setF] = React.useState({
    name: "", phone: "", service_type: "", base_fee: "", per_km_fee: "",
    is_always_available: false, active: true, notes: "", ...rider,
  });
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  async function save() {
    if (!f.name) return;
    if (f.phone && !validatePhone(f.phone)) {
      toast.push({ tone: "danger", title: "Invalid phone number", msg: "Enter a valid PH mobile or landline number." });
      return;
    }
    setBusy(true);
    const res = isNew ? await Riders.create(f) : await Riders.update(rider.id, f);
    setBusy(false);
    if (!res.error) onSaved();
  }

  return (
    <Drawer
      title={isNew ? "New rider" : `Edit · ${rider.name}`}
      onClose={onClose}
      footer={<><span className="spacer"/><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy || !f.name}>Save</button></>}
    >
      <Field label="Name" required><input className="input" value={f.name} onChange={e => set("name", e.target.value)}/></Field>
      <Field label="Phone"><input className="input" value={f.phone || ""} onChange={e => set("phone", e.target.value)} placeholder="+63 9XX XXX XXXX"/></Field>
      <Field label="Service type" help="Free text — DB will validate against its own check constraint.">
        <input className="input" list="rider-service-types" value={f.service_type || ""} onChange={e => set("service_type", e.target.value)} placeholder="e.g. lalamove, grab, individual…"/>
        <datalist id="rider-service-types">{SERVICE_SUGGESTIONS.map(s => <option key={s} value={s}/>)}</datalist>
      </Field>
      <div className="grid c2" style={{ gap: 12 }}>
        <Field label="Base fee"><input className="input" type="number" step="0.01" value={f.base_fee ?? ""} onChange={e => set("base_fee", e.target.value)}/></Field>
        <Field label="Per-km fee"><input className="input" type="number" step="0.01" value={f.per_km_fee ?? ""} onChange={e => set("per_km_fee", e.target.value)}/></Field>
      </div>
      <Field label="Always available">
        <label className="row"><input type="checkbox" checked={!!f.is_always_available} onChange={e => set("is_always_available", e.target.checked)}/> Available any day/time</label>
      </Field>
      {!isNew && (
        <Field label="Active">
          <label className="row"><input type="checkbox" checked={f.active !== false} onChange={e => set("active", e.target.checked)}/> Rider can be assigned to errands</label>
        </Field>
      )}
      <Field label="Notes"><textarea className="textarea" value={f.notes || ""} onChange={e => set("notes", e.target.value)}/></Field>
    </Drawer>
  );
}

/* ---------- Section C — Assigned deliveries ------------------------------ */
function AssignedDeliveriesSection({ rider }) {
  const [rows, setRows] = React.useState(null);

  const load = React.useCallback(() => {
    Riders.deliveries(rider.id).then(r => setRows(r.data || []));
  }, [rider.id]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div className="card">
      <div className="card-head row between" style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontWeight: 700 }}>Assigned deliveries</div>
      </div>
      <div className="tbl-wrap">
        {rows === null ? <Spinner/> :
         rows.length === 0 ? <EmptyState title="No deliveries" hint="Orders assigned to this rider will appear here."/> :
         <table className="tbl">
           <thead><tr><th>Order #</th><th>Customer</th><th>Status</th><th>Delivery date</th><th style={{ textAlign: "right" }}>Total</th></tr></thead>
           <tbody>{rows.map(r => (
             <tr key={r.id}>
               <td className="mono" style={{ fontSize: 12 }}>{r.order_number || r.id?.slice(0, 8)}</td>
               <td style={{ fontWeight: 600 }}>{r.contact_name || "—"}</td>
               <td><Pill tone={statusTone(r.status)}>{humanize(r.status || "—")}</Pill></td>
               <td>{formatDate(r.delivery_date)}</td>
               <td className="amt" style={{ textAlign: "right" }}>{formatMoney(r.total)}</td>
             </tr>))}</tbody>
         </table>}
      </div>
    </div>
  );
}

/* ---------- Section B — Weekly availability ------------------------------ */
function AvailabilitySection({ rider }) {
  const [rows, setRows] = React.useState(null);
  const [editing, setEditing] = React.useState(null); // day or "new"
  const [confirmRemoveDay, setConfirmRemoveDay] = React.useState(null);

  const load = React.useCallback(() => {
    Riders.availability(rider.id).then(r => {
      const data = r.data || [];
      data.sort((a, b) => DAYS.indexOf(a.day_of_week) - DAYS.indexOf(b.day_of_week));
      setRows(data);
    });
  }, [rider.id]);

  React.useEffect(() => { load(); }, [load]);

  async function doRemoveDay(day) {
    const res = await Riders.deleteAvailability(rider.id, day);
    if (!res.error) load();
  }

  return (
    <div className="card">
      <div className="card-head row between" style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontWeight: 700 }}>Weekly availability</div>
        <button className="btn btn-ghost" onClick={() => setEditing("new")}>{I.plus }Add day</button>
      </div>
      <div className="tbl-wrap">
        {rows === null ? <Spinner/> :
         rows.length === 0 ? <EmptyState title="No schedule" hint="Add the days this rider is available."/> :
         <table className="tbl">
           <thead><tr><th>Day</th><th>Start</th><th>End</th><th>Notes</th><th></th></tr></thead>
           <tbody>{rows.map(r => (
             <tr key={r.id}>
               <td style={{ fontWeight: 600 }}>{humanize(r.day_of_week)}</td>
               <td className="mono">{r.start_time || "—"}</td>
               <td className="mono">{r.end_time || "—"}</td>
               <td>{r.notes || "—"}</td>
               <td className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
                 <button className="btn btn-ghost" onClick={() => setEditing(r)}>Edit</button>
                 <button className="btn btn-ghost" onClick={() => setConfirmRemoveDay(r.day_of_week)}>Remove</button>
               </td>
             </tr>))}</tbody>
         </table>}
      </div>
      {editing && (
        <AvailabilityDrawer
          rider={rider}
          row={editing === "new" ? null : editing}
          existingDays={(rows || []).map(r => r.day_of_week)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {confirmRemoveDay && (
        <ConfirmModal title="Remove availability"
          message={`Remove availability for ${humanize(confirmRemoveDay)}?`}
          confirmLabel="Remove" danger
          onConfirm={() => doRemoveDay(confirmRemoveDay)}
          onClose={() => setConfirmRemoveDay(null)} />
      )}
    </div>
  );
}

function AvailabilityDrawer({ rider, row, existingDays, onClose, onSaved }) {
  const isNew = !row;
  const [f, setF] = React.useState({
    day_of_week: row?.day_of_week || DAYS.find(d => !existingDays.includes(d)) || "monday",
    start_time: row?.start_time || "",
    end_time: row?.end_time || "",
    notes: row?.notes || "",
  });
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  async function save() {
    setBusy(true);
    const res = await Riders.upsertAvailability(rider.id, f.day_of_week, f.start_time, f.end_time, f.notes);
    setBusy(false);
    if (!res.error) onSaved();
  }

  return (
    <Drawer
      title={isNew ? "Add availability" : `Edit · ${humanize(f.day_of_week)}`}
      onClose={onClose}
      footer={<><span className="spacer"/><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>Save</button></>}
    >
      <Field label="Day">
        <select className="select" value={f.day_of_week} onChange={e => set("day_of_week", e.target.value)} disabled={!isNew}>
          {DAYS.map(d => <option key={d} value={d}>{humanize(d)}</option>)}
        </select>
      </Field>
      <div className="grid c2" style={{ gap: 12 }}>
        <Field label="Start time"><input className="input" type="time" value={f.start_time} onChange={e => set("start_time", e.target.value)}/></Field>
        <Field label="End time"><input className="input" type="time" value={f.end_time} onChange={e => set("end_time", e.target.value)}/></Field>
      </div>
      <Field label="Notes"><textarea className="textarea" value={f.notes} onChange={e => set("notes", e.target.value)}/></Field>
    </Drawer>
  );
}

/* ---------- Section C — Errands ------------------------------------------ */
function ErrandsSection({ rider }) {
  const [rows, setRows] = React.useState(null);
  const [editing, setEditing] = React.useState(null); // errand or "new"
  const [filters, setFilters] = React.useState({ q: "", errand_type: "", status: "", from: "", to: "" });
  const [confirmDeleteErrand, setConfirmDeleteErrand] = React.useState(null);

  const load = React.useCallback(() => {
    Riders.errands(rider.id).then(r => setRows(r.data || []));
  }, [rider.id]);

  React.useEffect(() => { load(); }, [load]);

  async function doRemoveErrand(errand) {
    const res = await Riders.removeErrand(errand.id);
    if (!res.error) load();
  }

  function statusToneFor(s) {
    if (s === "completed") return "success";
    if (s === "in_progress") return "warn";
    if (s === "cancelled") return "danger";
    return "info";
  }

  const filterDefs = React.useMemo(() => ([
    { type: "search", key: "q", placeholder: "Search description…", fields: ["description", "order_number"] },
    { type: "select", key: "errand_type", label: "All types",     options: ENUM_OPTIONS.errand_type },
    { type: "select", key: "status",      label: "All statuses",  options: ENUM_OPTIONS.errand_status },
    { type: "date",   key: "from", label: "From", column: "date" },
    { type: "date",   key: "to",   label: "To",   column: "date" },
  ]), []);
  const filtered = useFilteredRows(rows, filterDefs, filters, { dateColumn: "date" });

  return (
    <div className="card">
      <div className="card-head row between" style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontWeight: 700 }}>Errands</div>
        <button className="btn btn-ghost" onClick={() => setEditing("new")}>{I.plus }New errand</button>
      </div>
      <div style={{ padding: "12px 16px 0" }}>
        <FilterBar filters={filterDefs} values={filters} onChange={setFilters}/>
      </div>
      <div className="tbl-wrap">
        {rows === null ? <Spinner/> :
         (filtered || []).length === 0 ? <EmptyState title="No errands" hint={rows.length ? "No errands match the current filters." : "Errands assigned to this rider will appear here."}/> :
         <table className="tbl">
           <thead><tr><th>Date</th><th>Type</th><th>Status</th><th>Description</th><th>Budget</th><th>Actual</th><th>Order #</th><th>Receipt</th><th></th></tr></thead>
           <tbody>{filtered.map(r => (
             <tr key={r.id} onClick={() => setEditing(r)}>
               <td>{formatDate(r.date)}</td>
               <td><Pill>{humanize(r.errand_type || "—")}</Pill></td>
               <td><Pill tone={statusToneFor(r.status)}>{humanize(r.status || "—")}</Pill></td>
               <td>{r.description || "—"}</td>
               <td>{r.budget != null ? formatMoney(r.budget) : "—"}</td>
               <td>{r.actual_cost != null ? formatMoney(r.actual_cost) : "—"}</td>
               <td className="mono" style={{ fontSize: 12 }}>{r.order_number || "—"}</td>
               <td className="mono" style={{ fontSize: 12 }}>{r.receipt_invoice || r.or_invoice_number || "—"}</td>
               <td onClick={(e) => e.stopPropagation()}>
                 <button className="btn btn-ghost" onClick={() => setConfirmDeleteErrand(r)}>Delete</button>
               </td>
             </tr>))}</tbody>
         </table>}
      </div>
      {editing && (
        <ErrandDrawer
          rider={rider}
          errand={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {confirmDeleteErrand && (
        <ConfirmModal title="Delete errand" message="Delete this errand? This cannot be undone."
          confirmLabel="Delete" danger
          onConfirm={() => doRemoveErrand(confirmDeleteErrand)}
          onClose={() => setConfirmDeleteErrand(null)} />
      )}
    </div>
  );
}

function ErrandDrawer({ rider, errand, onClose, onSaved }) {
  const isNew = !errand;
  const [f, setF] = React.useState({
    date: errand?.date || new Date().toISOString().slice(0, 10),
    errand_type: errand?.errand_type || "",
    status: errand?.status || "pending",
    description: errand?.description || "",
    budget: errand?.budget ?? "",
    actual_cost: errand?.actual_cost ?? "",
    order_id: errand?.order_id || "",
    expense_receipt_id: errand?.expense_receipt_id || "",
    notes: errand?.notes || "",
  });
  const [orders, setOrders] = React.useState([]);
  const [receipts, setReceipts] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  React.useEffect(() => {
    Riders.ordersLookup().then(r => setOrders(r.data || []));
    Riders.receiptsLookup().then(r => setReceipts(r.data || []));
  }, []);

  async function save() {
    setBusy(true);
    const res = isNew
      ? await Riders.createErrand(rider.id, f)
      : await Riders.updateErrand(errand.id, rider.id, f);
    setBusy(false);
    if (!res.error) onSaved();
  }

  return (
    <Drawer
      title={isNew ? "New errand" : "Edit errand"}
      onClose={onClose}
      footer={<><span className="spacer"/><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>Save</button></>}
    >
      <div className="grid c2" style={{ gap: 12 }}>
        <Field label="Date"><input className="input" type="date" value={f.date} onChange={e => set("date", e.target.value)}/></Field>
        <Field label="Errand type">
          <select className="select" value={f.errand_type} onChange={e => set("errand_type", e.target.value)}>
            <option value="">— Select type —</option>
            {ENUM_OPTIONS.errand_type.map(v => <option key={v} value={v}>{humanize(v)}</option>)}
          </select>
        </Field>
      </div>
      {!isNew && (
        <Field label="Status">
          <select className="select" value={f.status} onChange={e => set("status", e.target.value)}>
            {ENUM_OPTIONS.errand_status.map(v => <option key={v} value={v}>{humanize(v)}</option>)}
          </select>
        </Field>
      )}
      <Field label="Description"><input className="input" value={f.description} onChange={e => set("description", e.target.value)}/></Field>
      <div className="grid c2" style={{ gap: 12 }}>
        <Field label="Budget"><input className="input" type="number" step="0.01" value={f.budget} onChange={e => set("budget", e.target.value)}/></Field>
        {!isNew && <Field label="Actual cost"><input className="input" type="number" step="0.01" value={f.actual_cost} onChange={e => set("actual_cost", e.target.value)}/></Field>}
      </div>
      <Field label="Linked order">
        <select className="select" value={f.order_id || ""} onChange={e => set("order_id", e.target.value)}>
          <option value="">— None —</option>
          {orders.map(o => <option key={o.id} value={o.id}>{o.order_number}</option>)}
        </select>
      </Field>
      {!isNew && (
        <Field label="Linked receipt">
          <select className="select" value={f.expense_receipt_id || ""} onChange={e => set("expense_receipt_id", e.target.value)}>
            <option value="">— None —</option>
            {receipts.map(r => <option key={r.id} value={r.id}>{r.or_invoice_number || "(no #)"} · {r.vendor_name || ""}</option>)}
          </select>
        </Field>
      )}
      <Field label="Notes"><textarea className="textarea" value={f.notes} onChange={e => set("notes", e.target.value)}/></Field>
    </Drawer>
  );
}

export { RidersView };
